/**
 * The uniform Workpay response envelope and the normalised error every API
 * call rejects with. Shared by every app that talks to a Workpay backend
 * (tenant-onboarding, ds-external) — pair this with `applyEnvelopeInterceptor`
 * from `./envelopeInterceptor`.
 */

/** Universal success status.code. Sourced from env so it is never hardcoded. */
export const SUCCESS_CODE = Number(import.meta.env.VITE_API_SUCCESS_CODE ?? 2000);

/** On-wire response shape emitted by every Workpay backend service. */
export interface ApiEnvelope<T> {
  status: {
    code: number;
    description: string;
    fields?: Record<string, string>;
  };
  data: T | null;
}

/**
 * Normalised error every non-success API outcome rejects with — whether it
 * arrived as a business error in an HTTP 200 envelope (Java/Go services) or
 * as a real 4xx/5xx from the api-gateway. `code === 0` is a synthetic
 * transport-failure code (no envelope on the wire).
 */
export class WorkpayApiError extends Error {
  constructor(
    readonly code: number,
    readonly description: string,
    readonly fields?: Record<string, string>,
    readonly httpStatus = 200,
  ) {
    super(description);
    this.name = 'WorkpayApiError';
  }
}

/** Unauthorized / session-gone family — triggers a login redirect. */
const UNAUTHORIZED_CODES = new Set([4010, 48010, 48011, 48012, 48013, 48014]);

/**
 * True for an unauthorized / session-gone outcome. Matches the envelope code
 * family; also treats a wire-level HTTP 401 as unauthorized so a bare
 * (non-enveloped) 401 still triggers the login flow.
 */
export const isUnauthorizedError = (e: unknown): e is WorkpayApiError =>
  e instanceof WorkpayApiError &&
  (UNAUTHORIZED_CODES.has(e.code) || e.httpStatus === 401);

/**
 * Forbidden / insufficient-permission outcome. Matches the envelope code
 * family (4030 service WP_FORBIDDEN, 48030 gateway WP_INSUFFICIENT_PERMISSIONS)
 * and also a wire-level HTTP 403 — so a bare, non-enveloped 403 (e.g. from
 * nginx / a WAF / a proxy upstream of the gateway) is still caught. Duck-typed
 * so it works on any error object, not only a WorkpayApiError instance.
 */
const FORBIDDEN_CODES = new Set([4030, 48030]);
export const isForbiddenError = (e: unknown): boolean => {
  if (!e || typeof e !== 'object') return false;
  const err = e as { code?: number; httpStatus?: number };
  return FORBIDDEN_CODES.has(err.code ?? -1) || err.httpStatus === 403;
};
